// Outlook-style "reading pane" mockup of the incident notification email a
// real on-call distribution list would receive when a major incident is
// declared. Purely decorative/local — no email is actually sent, no backend
// call is made. One caller: the outage-fanout demo surface wires this up
// after "Simulate Outage" alongside the ServiceNow/PagerDuty mockups.
// Styled to read as an actual email client chrome (header field rows,
// attachment chip), not a generic card — see incident-email-mockup.css.
import '../styles/incident-email-mockup.css';

export interface IncidentEmailMockupIncident {
  id: string;
  severity?: 'critical' | string | null;
  current_status?: string | null;
  impact_statement?: string | null;
  service_name?: string | null;
  affected_site?: string | null;
  estimated_users_impacted?: number | null;
  workaround?: string | null;
  is_demo?: boolean;
  state?: string | null;
  declared_at?: string | null;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'just now';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'just now';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = (n: number) => String(n).padStart(2, '0');
  let hours = d.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${pad(hours)}:${pad(d.getMinutes())} ${ampm}`;
}

function attachmentFilename(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  const src = isNaN(d.getTime()) ? new Date() : d;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `incident-report-${src.getFullYear()}${pad(src.getMonth() + 1)}${pad(src.getDate())}.pdf`;
}

function IcoPdf() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 2h8l5 5v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" fill="#D64541" />
      <path d="M14 2v4a1 1 0 0 0 1 1h4" fill="#B23328" />
      <text x="12" y="16.5" textAnchor="middle" fontSize="6.2" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">PDF</text>
    </svg>
  );
}

function IcoReply() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M6.5 3 2.5 7l4 4M2.5 7h6a4.5 4.5 0 0 1 4.5 4.5V13" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoReplyAll() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M9 3 5 7l4 4M5 7h6.5A3.5 3.5 0 0 1 15 10.5V12" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 3 1 7l4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoForward() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M9.5 3 13.5 7l-4 4M13.5 7h-6A4.5 4.5 0 0 0 3 11.5V13" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HeaderRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="email-mock-hrow">
      <span className="email-mock-hlabel">{label}</span>
      <span className="email-mock-hvalue">{value}</span>
    </div>
  );
}

export default function IncidentEmailMockup({ incident, onClose }: { incident: IncidentEmailMockupIncident; onClose: () => void }) {
  const subject = incident.service_name
    ? `[CRITICAL] ${incident.service_name} — Major Incident`
    : '[CRITICAL] Major Incident';
  const impact = incident.impact_statement || 'Impact assessment in progress.';
  const filename = attachmentFilename(incident.declared_at);
  const statusLabel = incident.current_status || 'Investigating';
  const usersLabel =
    typeof incident.estimated_users_impacted === 'number'
      ? incident.estimated_users_impacted.toLocaleString()
      : null;

  return (
    <div className="email-mock-overlay" onClick={onClose}>
      <div className="email-mock-card" onClick={(e) => e.stopPropagation()}>
        <div className="email-mock-ribbon">
          <div className="email-mock-ribbon-actions">
            <button className="email-mock-ribbon-btn" onClick={onClose}><IcoReply /> Reply</button>
            <button className="email-mock-ribbon-btn" onClick={onClose}><IcoReplyAll /> Reply All</button>
            <button className="email-mock-ribbon-btn" onClick={onClose}><IcoForward /> Forward</button>
          </div>
          <button className="email-mock-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="email-mock-subject">
          {subject}
          <span className="email-mock-severity-chip">{incident.severity || 'critical'}</span>
        </div>

        <div className="email-mock-headers">
          <div className="email-mock-avatar">AI</div>
          <div className="email-mock-hgroup">
            <HeaderRow label="From:" value="Atlas Incident Bot <incidents@atlas.local>" />
            <HeaderRow label="To:" value="oncall-dl@example.com, sre-lead@example.com" />
            <HeaderRow label="Cc:" value="engineering-leadership@example.com" />
            <HeaderRow label="Date:" value={formatDate(incident.declared_at)} />
          </div>
        </div>

        <div className="email-mock-body">
          <div className="email-mock-summary">
            <div className="email-mock-summary-item">
              <span className="email-mock-summary-label">Status</span>
              <span className="email-mock-summary-value">{statusLabel}</span>
            </div>
            <div className="email-mock-summary-item email-mock-summary-item--wide">
              <span className="email-mock-summary-label">Impact</span>
              <span className="email-mock-summary-value">{impact}</span>
            </div>
            {incident.affected_site && (
              <div className="email-mock-summary-item">
                <span className="email-mock-summary-label">Affected Site</span>
                <span className="email-mock-summary-value">{incident.affected_site}</span>
              </div>
            )}
            {usersLabel && (
              <div className="email-mock-summary-item">
                <span className="email-mock-summary-label">Users Impacted</span>
                <span className="email-mock-summary-value">{usersLabel}</span>
              </div>
            )}
          </div>

          {incident.workaround && (
            <div className="email-mock-workaround">
              <span className="email-mock-workaround-label">Workaround</span>
              <span className="email-mock-workaround-text">{incident.workaround}</span>
            </div>
          )}

          <p className="email-mock-para email-mock-eta">
            <strong>ETA:</strong> Investigating — ETA not yet estimated.
          </p>
          <p className="email-mock-para email-mock-eta">
            <strong>Next Update:</strong> Within 30 minutes, or upon status change.
          </p>
          <p className="email-mock-sig">
            — Atlas Incident Bot<br />
            This is an automated notification. Do not reply directly to this address.
          </p>
        </div>

        <div className="email-mock-attachments">
          <div className="email-mock-attachment-chip">
            <IcoPdf />
            <div className="email-mock-attachment-info">
              <span className="email-mock-attachment-name">{filename}</span>
              <span className="email-mock-attachment-size">184 KB</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
